import {
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    vec2,
    vec3,
} from 'gl-matrix';
import { VertexArrayObject } from '~/engine/core/utils/VertexArrayObject';
import { gl } from '~/engine/Engine';
import { DoseMatrix } from '../../../domain/patient/doseMatrix';
import { Edge } from '../common/Edge';
import { EnvironmentObject } from '../common/EnvironmentObject';
import { RotatedBoundingBox } from '../common/RotatedBoundingBox';
import { SliceMesh } from '../common/slicemesh/SliceMesh';
import { Vertex, VerticesToArrays } from '../common/slicemesh/Vertex';
import { DoseTexture } from './DoseTexture';

export const DEFAULT_DOSE_OPACITY_VALUE = 0.5;
export const DEFAULT_MIN_DOSE_RANGE = 0.25;
export const DEFAULT_MAX_DOSE_RANGE = 1;

/**
 * {@link DoseDistribution} is a data model for dose.\
 * It inherits from {@link EnvironmentObject} as it has physical properties and may be visualized.
 */
export class DoseDistribution extends EnvironmentObject {
    /**
     * {@link DoseTexture}, which stores the actual physical properties and {@link WebGLTexture texture} data of the {@link DoseDistribution}.
     */
    private texture: DoseTexture;

    /**
     * The parent DoseMatrix object.
     */
    private readonly _parent: DoseMatrix;
    public get parent(): DoseMatrix {
        return this._parent;
    }

    public get sopInstanceUID(): string {
        return this.parent.dicomUid;
    }

    /**
     * The selected dose range.
     */
    private _selectedDoseRange: ReadonlyVec2 = vec2.fromValues(
        DEFAULT_MIN_DOSE_RANGE,
        DEFAULT_MAX_DOSE_RANGE
    );
    public get selectedDoseRange(): ReadonlyVec2 {
        return this._selectedDoseRange;
    }

    public set selectedDoseRange(range: ReadonlyVec2) {
        this._selectedDoseRange = range;
    }

    /**
     * The visibility of the dose distribution.
     * Default value is true.
     */
    private _isVisible: boolean = true;
    public get isVisible(): boolean {
        return this._isVisible;
    }

    public set isVisible(visible: boolean) {
        this._isVisible = visible;
    }

    private _alphaLevelOpacity: number = DEFAULT_DOSE_OPACITY_VALUE;

    public get alphaLevelOpacity(): number {
        return this._alphaLevelOpacity;
    }

    public set alphaLevelOpacity(opacity: number) {
        this._alphaLevelOpacity = opacity;
    }

    /**
     * Constructor that will create a {@link DoseDistribution} from {@link Dose}.
     * @param dose The {@link Dose} datamodel that contains all the data needed for the creation of {@link DoseDistribution}.
     */
    public constructor(dose: DoseMatrix) {
        super();
        this.texture = new DoseTexture(dose);
        this._parent = dose;
    }

    public get boundingBox(): RotatedBoundingBox {
        return this.texture.boundingBox;
    }

    /**
     * Method to get the range of color values in the {@link DoseTexture}.
     */
    public get range(): ReadonlyVec2 {
        return this.texture.range;
    }

    /**
     * Method to bind the {@link texture DoseTexture}'s {@link DoseTexture.texture texture}.
     */
    public bindTexture(): void {
        this.texture.bindTexture();
    }

    /** Generate vertices for a z position */
    private generateVertices(zcoord: number, k: number): Vertex[] {
        const imageGrid = this.parent.grid;

        const vertices = [
            new Vertex(
                imageGrid.toWorldSpace(vec3.fromValues(0, 0, zcoord)),
                vec3.fromValues(0, 0, k)
            ),
            new Vertex(
                imageGrid.toWorldSpace(
                    vec3.fromValues(0, imageGrid.sizeY - 0.5, zcoord)
                ),
                vec3.fromValues(0, 1, k)
            ),
            new Vertex(
                imageGrid.toWorldSpace(
                    vec3.fromValues(
                        imageGrid.sizeX - 0.5,
                        imageGrid.sizeY - 0.5,
                        zcoord
                    )
                ),
                vec3.fromValues(1, 1, k)
            ),
            new Vertex(
                imageGrid.toWorldSpace(
                    vec3.fromValues(imageGrid.sizeX - 0.5, 0, zcoord)
                ),
                vec3.fromValues(1, 0, k)
            ),
        ];
        const registrationTransformation = this.transformationMatrix;
        if (registrationTransformation) {
            return vertices.map((v) => v.transform(registrationTransformation));
        }
        return vertices;
    }

    // cached image edges for cross section calculation
    private _doseMatrixEdges: Edge[] | undefined;
    private get doseMatrixEdges(): Edge[] {
        if (!this._doseMatrixEdges) {
            const imageGrid = this.parent.grid;
            const vertices = [
                ...this.generateVertices(-0.5, 0),
                ...this.generateVertices(imageGrid.sizeZ - 0.5, 1),
            ];
            this._doseMatrixEdges = [];
            this._doseMatrixEdges.push(
                new Edge(vertices[0], vertices[1]),
                new Edge(vertices[1], vertices[2]),
                new Edge(vertices[2], vertices[3]),
                new Edge(vertices[3], vertices[0])
            );
            this._doseMatrixEdges.push(
                new Edge(vertices[4], vertices[5]),
                new Edge(vertices[5], vertices[6]),
                new Edge(vertices[6], vertices[7]),
                new Edge(vertices[7], vertices[4])
            );
            this._doseMatrixEdges.push(
                new Edge(vertices[0], vertices[4]),
                new Edge(vertices[1], vertices[5]),
                new Edge(vertices[2], vertices[6]),
                new Edge(vertices[3], vertices[7])
            );
        }
        return this._doseMatrixEdges;
    }

    public crossSection(
        toProjection: ReadonlyMat4
    ): VertexArrayObject | undefined {
        const vertexCoordinates: number[] = [];
        const textureCoordinates: number[] = [];
        const indices: number[] = [];
        const intersections = SliceMesh.intersections(
            this.doseMatrixEdges,
            toProjection
        );
        if (intersections && intersections.length > 0) {
            const buffers = VerticesToArrays(intersections);
            indices.push(
                ...SliceMesh.tesselate(buffers[0], vertexCoordinates.length / 2)
            );
            vertexCoordinates.push(...buffers[0]);
            textureCoordinates.push(...buffers[1]);
        }
        if (indices.length === 0) {
            //if there's not a single intersection point, there is nothing to be rendered.
            return undefined;
        }
        const vao = new VertexArrayObject();
        vao.bind();
        vao.createAttribute(0, gl.FLOAT, vertexCoordinates, 2);
        vao.createAttribute(1, gl.FLOAT, textureCoordinates, 3);
        vao.createIndexBuffer(indices);
        vao.unbind();
        return vao;
    }

    public getDoseIntensityValueAtCursorCoordinate(
        patientCoords: ReadonlyVec3
    ): number | null {
        const cursorVoxelCoords = this.parent.grid.toVoxelSpace(patientCoords);
        const doseIntensityValueAtCoordinate =
            this.texture.getDoseIntensityValueAtCursorCoordinate(
                cursorVoxelCoords
            );
        return doseIntensityValueAtCoordinate;
    }
    /**
     * Method to delete the {@link texture DoseTexture}.
     */
    public delete(): void {
        this.texture.delete();
    }
}
